import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Award, Lock, Sparkles, Zap } from 'lucide-react';
import { getStudentAchievements, awardTeacherPick } from '../../api/achievements';
import errorText from '../../utils/errorText';
import { useI18n } from '../../i18n/I18nContext';
import './StudentAchievements.css';

/**
 * StudentAchievements — a student's achievements in CRM (GAMIFICATION_SPEC §6):
 * engagement risk with a suggested action, the collection with Davron medals,
 * the closest next steps and the "Ustoz tanlovi" award. Davron stays here (the medals are
 * what the student sees) though the rest of the CRM uses plain icons. Only the catalogue's
 * poses ship in public/achievements/davron/; a pose missing there falls back to an Award icon.
 */

const RARITY_CLASS = { COMMON: 'sa-common', RARE: 'sa-rare', EPIC: 'sa-epic', LEGENDARY: 'sa-legendary' };

export function Medal({ mascot, rarity, unlocked, secret, size = 52, title }) {
  const hidden = secret && !unlocked;
  const src = `/achievements/davron/${mascot}@256.webp`;
  const [brokenSrc, setBrokenSrc] = useState(null);
  return (
    <span
      className={`sa-medal ${unlocked ? RARITY_CLASS[rarity] || 'sa-common' : 'sa-locked'}`}
      style={{ width: size, height: size }}
      title={title}
    >
      <span className="sa-medal-disc">
        {hidden ? (
          <span className="sa-medal-q">?</span>
        ) : brokenSrc === src ? (
          <Award className="sa-medal-fallback" aria-hidden="true" />
        ) : (
          <img src={src} alt="" draggable={false} onError={() => setBrokenSrc(src)} />
        )}
      </span>
      {!unlocked && !hidden && (
        <span className="sa-medal-lock"><Lock size={10} /></span>
      )}
    </span>
  );
}

function StudentAchievements({ userId }) {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback((fresh = false) => {
    setError(null);
    getStudentAchievements(userId, lang, fresh)
      .then(setData)
      .catch((e) => setError(errorText(e, t('achievements.loadError'))));
  }, [userId, lang, t]);

  useEffect(() => { if (userId) load(); }, [userId, load]);

  const teacherPick = data?.items?.find((i) => i.id === 'TEACHER_PICK');
  const alreadyPicked = !!teacherPick?.unlocked;

  const submit = async (e) => {
    e.preventDefault();
    if (note.trim().length < 2) return;
    setSending(true);
    try {
      await awardTeacherPick(userId, note.trim(), lang);
      toast.success(t('achievements.sent'));
      setFormOpen(false);
      setNote('');
      load(true);
    } catch (err) {
      const code = err?.response?.data?.code || err?.response?.data?.error?.code || err?.data?.code;
      toast.error(err?.code === 409 || err?.response?.status === 409 || code === 'ALREADY_AWARDED'
        ? t('achievements.alreadyGiven')
        : errorText(err, t('achievements.failed')));
    } finally {
      setSending(false);
    }
  };

  if (error) {
    return (
      <section className="sd-card sa-card">
        <h4>{t('achievements.title')}</h4>
        <p className="sa-muted">{error}</p>
      </section>
    );
  }
  if (!data) {
    return (
      <section className="sd-card sa-card" aria-busy="true">
        <h4>{t('achievements.title')}</h4>
        <div className="sa-skeleton" />
      </section>
    );
  }

  const unlocked = data.items.filter((i) => i.unlocked)
    .sort((a, b) => (b.unlockedAt || '').localeCompare(a.unlockedAt || ''));
  const risk = data.risk;
  const pct = data.total ? Math.round((data.unlocked / data.total) * 100) : 0;

  return (
    <section className="sd-card sa-card">
      <div className="sa-head">
        <h4>{t('achievements.title')}</h4>
        {alreadyPicked ? (
          <span className="sa-picked"><Award size={14} /> {t('achievements.given')}</span>
        ) : (
          <button type="button" className="sa-give" onClick={() => setFormOpen((v) => !v)}>
            <Sparkles size={14} /> {t('achievements.give')}
          </button>
        )}
      </div>

      {formOpen && !alreadyPicked && (
        <form className="sa-form" onSubmit={submit}>
          <img src="/achievements/davron/star@256.webp" alt="" className="sa-form-davron" />
          <div className="sa-form-body">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={280}
              rows={3}
              placeholder={t('achievements.notePlaceholder')}
              autoFocus
            />
            <div className="sa-form-actions">
              <span className="sa-muted">{note.length}/280</span>
              <button type="button" className="sa-btn-ghost" onClick={() => setFormOpen(false)}>{t('achievements.cancel')}</button>
              <button type="submit" className="sa-btn" disabled={sending || note.trim().length < 2}>
                {sending ? t('achievements.sending') : t('achievements.send')}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Risk ------------------------------------------------------------- */}
      <div className={`sa-risk sa-risk-${(risk.band || 'LOW').toLowerCase()}`}>
        <div className="sa-risk-score">
          <span className="sa-risk-num">{risk.score}</span>
          <span className="sa-risk-band">{t(`achievements.risk${risk.band}`)}</span>
        </div>
        <div className="sa-risk-body">
          <span className="sa-risk-title">{t('achievements.risk')}</span>
          <span className="sa-risk-meta">
            {risk.daysInactive === null
              ? t('achievements.neverActive')
              : risk.daysInactive === 0
                ? t('achievements.activeToday')
                : t('achievements.inactiveDays', { days: risk.daysInactive })}
            {' · '}
            {t('achievements.activeDays', { days: risk.activeDays28 })}
          </span>
          <span className="sa-risk-action"><strong>{t('achievements.suggestion')}:</strong> {risk.action?.text}</span>
        </div>
      </div>

      {/* Collection ------------------------------------------------------- */}
      <div className="sa-count">
        <div className="sa-count-bar"><div style={{ width: `${Math.max(2, pct)}%` }} /></div>
        <span>{t('achievements.collected', { unlocked: data.unlocked, total: data.total })}</span>
        {data.combo?.best > 0 && (
          <span className="sa-combo"><Zap size={13} /> {t('achievements.combo', { best: data.combo.best })}</span>
        )}
      </div>

      {unlocked.length > 0 ? (
        <>
          <p className="sa-label">{t('achievements.unlocked')}</p>
          <div className="sa-grid">
            {unlocked.map((a) => (
              <div key={a.id} className="sa-item" title={a.description || a.name}>
                <Medal mascot={a.mascot} rarity={a.rarity} unlocked title={a.rarityName} />
                <span className="sa-item-name">{a.name}</span>
                {a.note && <span className="sa-item-note">“{a.note}”</span>}
              </div>
            ))}
          </div>
        </>
      ) : (
        <p className="sa-muted">{t('achievements.none')}</p>
      )}

      {data.nextUp?.length > 0 && (
        <>
          <p className="sa-label">{t('achievements.nextUp')}</p>
          <ul className="sa-next">
            {data.nextUp.map((n) => (
              <li key={n.id}>
                <Medal mascot={n.mascot} rarity={n.rarity} unlocked={false} size={34} />
                <span className="sa-next-name">{n.name}</span>
                <span className="sa-next-bar"><span style={{ width: `${Math.max(4, Math.round((n.current / n.target) * 100))}%` }} /></span>
                <span className="sa-muted">
                  {n.current}/{n.target}{n.etaDays ? ` · ${t('achievements.eta', { days: n.etaDays })}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

export default StudentAchievements;
